"use client";

import { useSyncExternalStore } from "react";
import { CONSENT_COOKIE, CONSENT_MAX_AGE_SECONDS, COOKIEYES_CONSENT_COOKIE, NO_TRACKING_CONSENT, readConsentDecision, serializeConsentCookie, type ConsentDecision, type TrackingConsent } from "@/utils/tracking/consent";
import { canLoadBrowserAdTags } from "@/utils/tracking/environment";
import { updateGoogleConsent } from "@/utils/tracking/googleTag";

// The one source of consent in the browser. The cookie banner writes the
// choice through saveTrackingConsent; PostHog (posthog-browser.ts), the ad pixels,
// GA4, AttributionOnConsent and the buy buttons read it here. The server
// reads the same cookie (readTrackingConsent).

// Cookies the ad and analytics tags set in this browser; cleared when that
// consent is withdrawn. The proxy clears the attribution cookies itself.
const ADVERTISING_COOKIES = ["_fbp", "_fbc", "ttclid", "_ttp", "_gcl_au", "_gcl_aw", "_gcl_gb", "_gcl_gs", "_gcl_dc"];
const ANALYTICS_COOKIE_PATTERN = /^_ga(_.+)?$/;
// PostHog's identity: `ph_<token>_posthog` (cookie and localStorage) and its
// window keys in sessionStorage. Its own consent key `__ph_opt_in_out_<token>`
// stays: after a withdrawal it holds "denied" (posthog-consent.ts), which
// posthog-js reads on every capture, so nothing more is sent, not even the
// reload's $pageleave.
const POSTHOG_KEY_PATTERN = /^ph_/;

// undefined: not read yet. null: the visitor has not chosen.
let decision: ConsentDecision | null | undefined;
let snapshot: TrackingConsent = NO_TRACKING_CONSENT;
const listeners = new Set<() => void>();
const withdrawalHandlers = new Set<() => void>();
let isListening = false;
function readDocumentCookies(): Map<string, string> {
  const jar = new Map<string, string>();
  for (const part of document.cookie.split(";")) {
    const separator = part.indexOf("=");
    if (separator <= 0) continue;
    const name = part.slice(0, separator).trim();
    if (!jar.has(name)) jar.set(name, part.slice(separator + 1).trim());
  }
  return jar;
}
function readDecision(): ConsentDecision | null {
  try {
    const jar = readDocumentCookies();
    return readConsentDecision({
      get: name => jar.has(name) ? {
        value: jar.get(name)!
      } : undefined
    });
  } catch {
    return null;
  }
}

// Every browser ad tag and ad event checks `advertisement` from here. Outside
// production it always reads as "no", so local dev and preview deployments
// load no ad pixel and send no ad event. Analytics consent is unaffected.
function withAdEnvironment(consent: TrackingConsent): TrackingConsent {
  if (!consent.advertisement || canLoadBrowserAdTags()) return consent;
  return {
    ...consent,
    advertisement: false
  };
}
function isSameConsent(a: TrackingConsent | undefined, b: TrackingConsent | undefined): boolean {
  return a?.advertisement === b?.advertisement && a?.analytics === b?.analytics;
}
function isWithdrawal(previous: TrackingConsent | undefined, next: TrackingConsent): boolean {
  return previous?.advertisement === true && !next.advertisement || previous?.analytics === true && !next.analytics;
}
function expireCookie(name: string): void {
  const host = window.location.hostname;
  const domains = ["", `; domain=${host}`];
  const parts = host.split(".");
  if (parts.length > 2) domains.push(`; domain=.${parts.slice(-2).join(".")}`);else domains.push(`; domain=.${host}`);
  for (const domain of domains) {
    document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
  }
}
function clearStorageKeys(storage: Storage, pattern: RegExp): void {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && pattern.test(key)) keys.push(key);
  }
  keys.forEach(key => storage.removeItem(key));
}

// Cleanup is best effort, one step at a time: where cookies or storage are
// denied, their getters and setters throw (SecurityError), and that must not
// stop the rest of the cleanup, the withdrawal or its reload.
function attempt(step: () => void): void {
  try {
    step();
  } catch {
    // Denied cookies or storage: skip this step.
  }
}

/**
 * Clears what the analytics tags stored in this browser: the `_ga*` and
 * PostHog cookies and PostHog's identity in local and session storage.
 * Without analytics consent PostHog never starts (posthog-browser.ts), so
 * nothing else would clean them. Never throws.
 */
export function clearAnalyticsStorage(): void {
  const names: string[] = [];
  attempt(() => {
    for (const name of Array.from(readDocumentCookies().keys())) {
      if (ANALYTICS_COOKIE_PATTERN.test(name) || POSTHOG_KEY_PATTERN.test(name)) names.push(name);
    }
  });
  attempt(() => clearStorageKeys(window.localStorage, POSTHOG_KEY_PATTERN));
  attempt(() => clearStorageKeys(window.sessionStorage, POSTHOG_KEY_PATTERN));
  names.forEach(name => attempt(() => expireCookie(name)));
}
function clearWithdrawnStorage(next: TrackingConsent): void {
  if (!next.advertisement) ADVERTISING_COOKIES.forEach(name => attempt(() => expireCookie(name)));
  if (!next.analytics) clearAnalyticsStorage();
}

/**
 * A consent that was given is gone: tell Google it is denied, let the
 * handlers stop what they run (PostHog opts out), clear that consent's
 * cookies and storage, and reload once, because loaded pixels cannot be
 * unloaded. Nothing is written in place of a lost choice, so the reloaded
 * page asks again.
 */
function withdraw(next: TrackingConsent): void {
  attempt(() => updateGoogleConsent(withAdEnvironment(next)));
  // A failing handler must not keep the withdrawn tags running.
  withdrawalHandlers.forEach(handler => attempt(handler));
  attempt(() => clearWithdrawnStorage(next));
  window.location.reload();
}

// Consent Mode gets what the tags may actually do here, so a preview never
// grants ad storage to the GA4 tag.
function applyDecision(next: ConsentDecision | null): void {
  decision = next;
  snapshot = withAdEnvironment(next?.consent ?? NO_TRACKING_CONSENT);
  if (next) updateGoogleConsent(snapshot);
}
function ensureRead(): void {
  if (decision === undefined) applyDecision(readDecision());
}
function notify(): void {
  listeners.forEach(listener => listener());
}

// A choice made in another tab, or a consent cookie that expired, was
// removed or became unreadable: pick it up when this tab is shown again.
// Losing a choice that granted anything is a withdrawal.
function handleVisibilityChange(): void {
  if (document.visibilityState !== "visible" || decision === undefined) return;
  const previous = decision;
  const next = readDecision();
  if (previous === null === (next === null) && isSameConsent(previous?.consent, next?.consent)) {
    return;
  }
  applyDecision(next);
  const nextConsent = next?.consent ?? NO_TRACKING_CONSENT;
  if (isWithdrawal(previous?.consent, nextConsent)) {
    withdraw(nextConsent);
    return;
  }
  notify();
}

/** Calls `listener` whenever the consent or the decision changes. */
export function subscribeTrackingConsent(listener: () => void): () => void {
  listeners.add(listener);
  if (!isListening) {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    isListening = true;
  }
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Runs `handler` synchronously when a consent is withdrawn, before its
 * storage is cleared and the page reloads.
 */
export function onTrackingConsentWithdrawal(handler: () => void): () => void {
  withdrawalHandlers.add(handler);
  return () => {
    withdrawalHandlers.delete(handler);
  };
}

/** Current consent, for event handlers outside React render. */
export function getTrackingConsent(): TrackingConsent {
  if (typeof window === "undefined") return NO_TRACKING_CONSENT;
  ensureRead();
  return snapshot;
}

/** Whether the visitor has chosen (in our banner, or earlier in CookieYes). */
export function hasConsentDecision(): boolean {
  if (typeof window === "undefined") return true;
  ensureRead();
  return decision !== null;
}

/** The visitor's choice before any environment rule, for the settings view. */
export function getConsentChoice(): TrackingConsent | null {
  if (typeof window === "undefined") return null;
  ensureRead();
  return decision?.consent ?? null;
}

/**
 * Stores a choice from the banner. Our cookie then wins over a CookieYes
 * choice, which is removed. Withdrawing a consent goes through withdraw().
 */
export function saveTrackingConsent(consent: TrackingConsent): void {
  ensureRead();
  const previous = decision?.consent;
  const now = Math.floor(Date.now() / 1000);
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  // Where cookies are denied the choice holds for this page only.
  attempt(() => {
    document.cookie = `${CONSENT_COOKIE}=${serializeConsentCookie(consent, now)}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; path=/; SameSite=Lax${secure}`;
  });
  attempt(() => expireCookie(COOKIEYES_CONSENT_COOKIE));
  applyDecision({
    consent: {
      ...consent
    },
    source: "guestgallery"
  });
  if (isWithdrawal(previous, consent)) {
    withdraw(consent);
    return;
  }
  notify();
}
function getServerSnapshot(): TrackingConsent {
  return NO_TRACKING_CONSENT;
}

/**
 * Consent from our banner (or a CookieYes choice), updated live when the
 * visitor chooses. Server render and hydration always see "no consent".
 */
export function useTrackingConsent() {
  return useSyncExternalStore(subscribeTrackingConsent, getTrackingConsent, getServerSnapshot);
}

/** False until the visitor has chosen; true on the server, so no banner renders there. */
export function useHasConsentDecision() {
  return useSyncExternalStore(subscribeTrackingConsent, hasConsentDecision, _temp);
}
function _temp() {
  return true;
}